import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown, Edit2, RotateCcw } from 'lucide-react';
import ComponentIcon from './ComponentIcon';

const FloorPlanView = ({ facilityId, isAdmin = false, onEdit }) => {
  const [buildings, setBuildings] = useState([]);
  const [floorplans, setFloorplans] = useState([]);
  const [components, setComponents] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedFloorplan, setSelectedFloorplan] = useState(null);
  const [currentFloorplanData, setCurrentFloorplanData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch buildings for facility
  useEffect(() => {
    if (!facilityId) return;

    const fetchBuildings = async () => {
      try {
        const res = await fetch(`/api/facilities/${facilityId}/buildings`);
        const data = await res.json();
        setBuildings(data);
        if (data.length > 0 && !selectedBuilding) {
          setSelectedBuilding(data[0]._id);
        }
      } catch (error) {
        console.error('Error fetching buildings:', error);
      }
    };

    const fetchComponents = async () => {
      try {
        const res = await fetch(`/api/facilities/${facilityId}/components`);
        const data = await res.json();
        setComponents(data);
      } catch (error) {
        console.error('Error fetching components:', error);
      }
    };

    fetchBuildings();
    fetchComponents();

    // Poll for component status updates
    const interval = setInterval(fetchComponents, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  // Fetch floorplans when building changes
  useEffect(() => {
    if (!selectedBuilding) return;

    const fetchFloorplans = async () => {
      try {
        const res = await fetch(`/api/buildings/${selectedBuilding}/floorplans`);
        const data = await res.json();
        setFloorplans(data);
        if (data.length > 0) {
          setSelectedFloorplan(data[0]._id);
        } else {
          setSelectedFloorplan(null);
          setCurrentFloorplanData(null);
        }
      } catch (error) {
        console.error('Error fetching floorplans:', error);
      }
    };

    fetchFloorplans();
  }, [selectedBuilding]);

  // Fetch floorplan image when selection changes
  useEffect(() => {
    if (!selectedFloorplan) {
      setCurrentFloorplanData(null);
      setLoading(false);
      return;
    }

    const fetchFloorplan = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/floorplans/${selectedFloorplan}`);
        const data = await res.json();
        setCurrentFloorplanData(data);
      } catch (error) {
        console.error('Error fetching floorplan:', error);
      }
      setLoading(false);
    };

    fetchFloorplan();
  }, [selectedFloorplan]);

  // Get components placed on current floorplan
  const placedComponents = components.filter(
    c => c.floorPlanId === selectedFloorplan && c.x !== null && c.y !== null
  );

  // Check for alarms in other floorplans/buildings
  const hasAlarmsElsewhere = components.some(
    c => c.currentStatus !== 'normal' && c.floorPlanId !== selectedFloorplan
  );

  const handleResetAll = async () => {
    try {
      await fetch(`/api/facilities/${facilityId}/components/reset-all`, { method: 'POST' });
      // Refetch components
      const res = await fetch(`/api/facilities/${facilityId}/components`);
      const data = await res.json();
      setComponents(data);
    } catch (error) {
      console.error('Error resetting components:', error);
    }
  };

  if (buildings.length === 0) {
    return (
      <div className="bg-white/50 rounded-xl p-8 text-center text-[#433225]/60">
        <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Keine Gebäudepläne vorhanden</p>
        {isAdmin && (
          <button
            onClick={onEdit}
            className="mt-4 px-4 py-2 bg-[#e89f71] text-white rounded-lg hover:bg-[#d88f61]"
          >
            Lagepläne einrichten
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white/50 rounded-xl p-4">
      {/* Header with selectors */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-[#433225]/70">Gebäude:</label>
          <div className="relative">
            <select
              value={selectedBuilding || ''}
              onChange={(e) => setSelectedBuilding(e.target.value)}
              className="appearance-none px-4 py-2 pr-8 rounded-lg bg-white text-[#433225] border border-[#e8d5c7] focus:ring-2 focus:ring-[#e89f71]"
            >
              {buildings.map(b => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#433225]/50 pointer-events-none" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-[#433225]/70">Etage:</label>
          <div className="relative">
            <select
              value={selectedFloorplan || ''}
              onChange={(e) => setSelectedFloorplan(e.target.value)}
              className="appearance-none px-4 py-2 pr-8 rounded-lg bg-white text-[#433225] border border-[#e8d5c7] focus:ring-2 focus:ring-[#e89f71]"
              disabled={floorplans.length === 0}
            >
              {floorplans.length === 0 ? (
                <option value="">Keine Pläne</option>
              ) : (
                floorplans.map(fp => (
                  <option key={fp._id} value={fp._id}>{fp.name}</option>
                ))
              )}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#433225]/50 pointer-events-none" />
            {hasAlarmsElsewhere && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            )}
          </div>
        </div>

        <div className="flex-1" />

        {/* Reset button */}
        {components.some(c => c.currentStatus !== 'normal') && (
          <button
            onClick={handleResetAll}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-[#e8d5c7] text-[#433225] hover:bg-[#dcc5b7]"
            title="Alle Status zurücksetzen"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        )}

        {/* Edit button for admin */}
        {isAdmin && (
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-[#e89f71] text-white hover:bg-[#d88f61]"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Floorplan display */}
      <div className="bg-[#f0f0f0] rounded-lg overflow-auto flex justify-center" style={{ minHeight: 300 }}>
        {loading ? (
          <div className="flex items-center justify-center w-full" style={{ minHeight: 300 }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e89f71]" />
          </div>
        ) : currentFloorplanData ? (
          <div className="relative" style={{ width: 'fit-content' }}>
            {/* Background image */}
            <img
              src={`data:${currentFloorplanData.imageType};base64,${currentFloorplanData.imageData}`}
              alt={currentFloorplanData.name}
              className="block max-w-full h-auto"
              style={{ maxHeight: '50vh' }}
            />

            {/* Component overlays - positioned relative to image */}
            {placedComponents.map(comp => (
              <div
                key={comp._id}
                className="absolute"
                style={{
                  left: `${comp.x}%`,
                  top: `${comp.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
                title={`${comp.name} (${comp.area})`}
              >
                <ComponentIcon
                  type={comp.type}
                  number={comp.number}
                  status={comp.currentStatus}
                  size={32}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[#433225]/50 py-20">
            Kein Plan ausgewählt
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-4 text-sm text-[#433225]/70">
        <div className="flex items-center gap-2">
          <ComponentIcon type="smoke_detector" size={20} />
          <span>Rauchmelder</span>
        </div>
        <div className="flex items-center gap-2">
          <ComponentIcon type="manual_call_point" size={20} />
          <span>Handtaster</span>
        </div>
        <div className="flex items-center gap-2">
          <ComponentIcon type="siren" size={20} />
          <span>Sirene</span>
        </div>
        <div className="flex items-center gap-2">
          <ComponentIcon type="motion" size={20} />
          <span>Bewegungsmelder</span>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanView;
